/** Human-friendly "last seen" formatter. Mirrors the WhatsApp / Slack
 *  pattern: "Online" trumps any timestamp; otherwise we show a relative
 *  "5 minutes ago" / "yesterday" / "Mar 5" string.
 *
 *  Pass the user's ``last_seen_at`` ISO string and ``is_online`` flag.
 *  Returns null when there's nothing meaningful to show (offline + no
 *  last_seen_at) so the caller can fall back to other UI. */
export function formatLastSeen(
  lastSeenAt: string | null | undefined,
  isOnline: boolean | undefined,
): string | null {
  if (isOnline) return "Online";
  if (!lastSeenAt) return null;
  const seen = new Date(lastSeenAt);
  if (Number.isNaN(seen.getTime())) return null;

  const diffMs = Date.now() - seen.getTime();
  const diffMin = Math.floor(diffMs / 60_000);

  if (diffMin < 1) return "Last seen just now";
  if (diffMin < 60) return `Last seen ${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `Last seen ${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay === 1) return "Last seen yesterday";
  if (diffDay < 7) return `Last seen ${diffDay} days ago`;
  // Past a week, dates beat relative copy — "12 days ago" is harder to
  // mentally place than "Apr 24".
  return `Last seen ${seen.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  })}`;
}

/** Short relative "5m ago" / "just now" for a timestamp. Null when there's
 *  nothing to show. */
export function relativeAgo(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return null;
  const s = Math.max(0, Math.floor((Date.now() - t.getTime()) / 1000));
  if (s < 45) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

/** Authentic presence label + tone for the 3-state model. ``active`` reads
 *  "Active now"; ``away`` reads "Away · active 5m ago" (real interaction);
 *  ``offline`` falls back to the "last seen" copy. Null when nothing to show. */
export function presenceLabel(
  status: "active" | "away" | "offline",
  lastActiveAt: string | null | undefined,
  lastSeenAt: string | null | undefined,
): { text: string; tone: "active" | "away" | "muted" } | null {
  if (status === "active") return { text: "Active now", tone: "active" };
  if (status === "away") {
    const rel = relativeAgo(lastActiveAt);
    return { text: rel ? `Away · active ${rel}` : "Away", tone: "away" };
  }
  const ls = formatLastSeen(lastSeenAt, false);
  return ls ? { text: ls, tone: "muted" } : null;
}
